import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Car,
  Calendar,
  DollarSign,
  TrendingUp,
  Plus,
  ChevronRight,
  Clock,
  Check,
  Loader2,
  Banknote,
  AlertTriangle,
} from "lucide-react";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import PremiumIcon from "../components/ui/PremiumIcon";

export default function HostDashboardPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith("en") ? enUS : es;
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalVehicles: 0,
    activeTrips: 0,
    pendingRequests: 0,
    totalEarnings: 0,
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  // Stripe Connect status — drives the onboarding nudge banner. We keep
  // this null until the live check resolves so we don't briefly flash the
  // banner to fully-onboarded hosts on page load.
  const [connectStatus, setConnectStatus] = useState(null);
  const [onboarding, setOnboarding] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchConnectStatus();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch host's vehicles
      const vehiclesRes = await api.get(`/vehicles/host/${user?.user_id}`);
      const vehicles = vehiclesRes.data || [];

      // Fetch host's bookings
      const bookingsRes = await api.get("/bookings?role=host");
      const bookings = bookingsRes.data || [];

      // Calculate stats
      const activeTrips = bookings.filter((b) => b.status === "active").length;
      const pendingRequests = bookings.filter((b) => b.status === "requested").length;
      const totalEarnings = bookings
        .filter((b) => b.payment_status === "paid")
        .reduce((sum, b) => sum + (b.base_price || 0), 0);

      setStats({
        totalVehicles: vehicles.length,
        activeTrips,
        pendingRequests,
        totalEarnings,
      });

      setRecentBookings(bookings.slice(0, 5));
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookingAction = async (bookingId, action) => {
    try {
      await api.put(`/bookings/${bookingId}/status`, { status: action });
      fetchDashboardData();
    } catch (error) {
      console.error("Error updating booking:", error);
    }
  };

  // Live Stripe Connect status. Used to render the orange "Conecta tu
  // cuenta de pagos" banner that nudges hosts to finish onboarding so
  // their listings actually accept bookings (the backend gate at
  // POST /api/bookings rejects with 503 otherwise).
  const fetchConnectStatus = async () => {
    try {
      const { data } = await api.get("/stripe/connect/status");
      setConnectStatus(data);
    } catch (e) {
      // 503 (Stripe not configured) → treat as "no status"; the banner is
      // hidden so we don't push hosts to a broken flow.
      setConnectStatus({ connected: false, _unavailable: true });
    }
  };

  const startOnboarding = async () => {
    setOnboarding(true);
    try {
      // If no Connect account exists yet, create one first.
      if (!connectStatus?.connected) {
        try {
          await api.post("/stripe/connect/account");
        } catch (_) {
          // Already created → harmless.
        }
      }
      const { data } = await api.post("/stripe/connect/onboarding-link");
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
    } catch (e) {
      // surface as a soft inline retry; no toast lib import here
      console.error("connect onboarding failed", e);
      alert(t("hostDash.connectFailed"));
    } finally {
      setOnboarding(false);
    }
  };

  // Banner visibility — only show when we have a definitive answer AND the
  // host is not fully payment-ready. `_unavailable` (Stripe not
  // configured) hides it so we don't nag in dev/local.
  const showConnectBanner =
    connectStatus
    && !connectStatus._unavailable
    && !(connectStatus.charges_enabled && connectStatus.payouts_enabled);

  const statCards = [
    {
      label: t("hostDash.myCars"),
      value: stats.totalVehicles,
      icon: Car,
      tone: "terracotta",
      link: "/host/listings",
    },
    {
      label: t("hostDash.activeTrips"),
      value: stats.activeTrips,
      icon: TrendingUp,
      tone: "blue",
    },
    {
      label: t("hostDash.pendingRequests"),
      value: stats.pendingRequests,
      icon: Clock,
      tone: "amber",
    },
    {
      label: t("hostDash.totalEarnings"),
      value: `$${stats.totalEarnings.toLocaleString()}`,
      icon: DollarSign,
      tone: "emerald",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" data-testid="host-dashboard-page">
      <div className="container-main py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-agave">
              {t("hostDash.title")}
            </h1>
            <p className="text-gray-600">{t("hostDash.welcome", { name: user?.name })}</p>
          </div>
          <Link to="/host/listings">
            <Button className="btn-primary" data-testid="add-vehicle-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t("hostDash.addCar")}
            </Button>
          </Link>
        </div>

        {/* Stripe Connect onboarding nudge. Hidden once the host is fully
            charges_enabled + payouts_enabled (or when Stripe is not
            configured in this environment). */}
        {showConnectBanner && (
          <div
            className="mb-8 rounded-2xl border border-amber-300 bg-amber-50/80 p-5 shadow-sm"
            data-testid="host-connect-banner"
            role="alert"
          >
            <div className="flex items-start gap-4">
              <PremiumIcon icon={AlertTriangle} variant="gradient" tone="amber" size="md" />
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-semibold text-amber-950 text-lg leading-tight">
                  {t("hostDash.connectTitle")}
                </h3>
                <p className="text-sm text-amber-900/85 mt-1 leading-relaxed">
                  {connectStatus?.connected
                    ? t("hostDash.connectAlmost")
                    : t("hostDash.connectNeeded")}
                  {" "}{t("hostDash.connectTakesMin")}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <Button
                    onClick={startOnboarding}
                    disabled={onboarding}
                    className="btn-primary"
                    data-testid="host-connect-onboard-btn"
                  >
                    {onboarding ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t("hostDash.connecting")}
                      </>
                    ) : connectStatus?.connected ? (
                      t("hostDash.continueVerification")
                    ) : (
                      t("hostDash.connectStripe")
                    )}
                  </Button>
                  <Link to="/host/payouts" className="text-sm text-amber-900 underline underline-offset-2">
                    {t("hostDash.viewDetails")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat, index) => (
            <div key={index} className="card-base group hover:-translate-y-0.5 transition-transform">
              <div className="flex items-center gap-3 mb-3">
                <PremiumIcon icon={stat.icon} variant="gradient" tone={stat.tone} size="md" />
              </div>
              <p className="text-2xl font-heading font-bold">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Recent Bookings */}
        <div className="card-base">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-semibold text-lg">{t("hostDash.recentBookings")}</h2>
            <Link to="/host/listings" className="text-terracotta text-sm hover:underline">
              {t("hostDash.seeAll")}
            </Link>
          </div>

          {recentBookings.length > 0 ? (
            <div className="space-y-4">
              {recentBookings.map((booking) => (
                <div
                  key={booking.booking_id}
                  className="flex items-center justify-between p-4 bg-sand rounded-xl"
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={booking.vehicle?.photos?.[0] || "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=100"}
                      alt=""
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div>
                      <p className="font-semibold">
                        {booking.vehicle?.make} {booking.vehicle?.model}
                      </p>
                      <p className="text-sm text-gray-500">
                        {format(new Date(booking.pickup_date), "dd MMM", { locale: dateLocale })} -{" "}
                        {format(new Date(booking.return_date), "dd MMM", { locale: dateLocale })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {booking.status === "requested" ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleBookingAction(booking.booking_id, "confirmed")}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`confirm-${booking.booking_id}`}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleBookingAction(booking.booking_id, "canceled")}
                          className="border-red-300 text-red-600"
                          data-testid={`decline-${booking.booking_id}`}
                        >
                          {t("hostDash.decline")}
                        </Button>
                      </>
                    ) : (
                      <Badge
                        className={
                          booking.status === "confirmed"
                            ? "status-confirmed"
                            : booking.status === "active"
                            ? "status-active"
                            : booking.status === "completed"
                            ? "status-completed"
                            : "status-canceled"
                        }
                      >
                        {booking.status === "confirmed"
                          ? t("hostDash.confirmed")
                          : booking.status === "active"
                          ? t("hostDash.inProgress")
                          : booking.status === "completed"
                          ? t("hostDash.completed")
                          : t("hostDash.canceled")}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">{t("hostDash.noRecent")}</p>
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <Link
            to="/host/listings"
            className="card-base flex items-center justify-between hover-lift"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-terracotta/10 rounded-xl flex items-center justify-center">
                <Car className="w-5 h-5 text-terracotta" />
              </div>
              <div>
                <p className="font-semibold">{t("hostDash.manageCars")}</p>
                <p className="text-sm text-gray-500">{t("hostDash.manageCarsSub")}</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </Link>

          <Link
            to="/host/calendar"
            className="card-base flex items-center justify-between hover-lift"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-agave/10 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-agave" />
              </div>
              <div>
                <p className="font-semibold">{t("hostDash.calendar")}</p>
                <p className="text-sm text-gray-500">{t("hostDash.calendarSub")}</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </Link>

          <Link
            to="/host/payouts"
            data-testid="host-payouts-link"
            className="card-base flex items-center justify-between hover-lift"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-terracotta/10 rounded-xl flex items-center justify-center">
                <Banknote className="w-5 h-5 text-terracotta" />
              </div>
              <div>
                <p className="font-semibold">{t("hostDash.payouts")}</p>
                <p className="text-sm text-gray-500">{t("hostDash.payoutsSub")}</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </Link>
        </div>
      </div>
    </div>
  );
}
